import React, { useState, useEffect } from 'react';
import { trackEvent } from '../utils/analytics';
import { useExecutiveMe } from '../executive/useExecutiveMe';
import { Link, useLocation } from 'react-router-dom';
import { LogIn, UserPlus, LogOut, Menu, X, ChevronDown, Headphones } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import { AonBackBar } from './AonBackBar';
import { scrollToTop } from '../utils/scrollToTop';

const Navbar = () => {
  const location = useLocation();
  const { user, logout, isAuthenticated, corporate } = useAuth();
  const [execMe] = useExecutiveMe();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const closeTimeoutRef = React.useRef(null);

  const libraryLinks = [
    { path: '/biases', label: 'Biases' },
    { path: '/rhetoric', label: 'Rhetoric' },
    { path: '/kinesics', label: 'Kinesics' },
    { path: '/speech-analyzer', label: 'Speech Analyser' }
  ];

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/modules', label: 'Courses' },
    { path: '/blog', label: 'Articles' },
    { path: '/books', label: 'Psychology' },
    { label: 'Apps. & Libraries', children: libraryLinks },
    { path: '/resources', label: 'Resources', children: [
      { path: '/private-journal', label: 'Private Journal' },
      { path: '/a-letter-to-your-future-self', label: 'A Letter to Your Future Self' },
      { path: '/words-of-wisdom', label: 'Quotes' }
    ] },
    { path: '/blogcast', label: 'Blogcast', icon: Headphones },
    { path: '/support-us', label: 'Donate' }
  ];

  const onNav = (path, where) => {
    scrollToTop();
    if (path === '/support-us') trackEvent('donate_click', { location: where });
    if (path === '/blogcast') trackEvent('blogcast_click', { location: where });
  };
  const groupActive = (link) => location.pathname === link.path || link.children.some((l) => location.pathname.startsWith(l.path));
  const [mobileOpen, setMobileOpen] = useState(null);
  const slug = (t) => t.toLowerCase().replace(/\s+/g, '-');

  const openLoginModal = () => {
    setAuthMode('login');
    setAuthModalOpen(true);
  };

  const openRegisterModal = () => {
    setAuthMode('register');
    setAuthModalOpen(true);
  };

  useEffect(() => {
    const onOpen = (e) => { setAuthMode(e.detail === 'register' ? 'register' : 'login'); setAuthModalOpen(true); };
    window.addEventListener('bwgela-open-auth', onOpen);
    return () => window.removeEventListener('bwgela-open-auth', onOpen);
  }, []);

  // Handle dropdown open with delay for robustness
  const handleDropdownEnter = (label) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenMenu(label);
  };

  const handleDropdownLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpenMenu(null);
    }, 150); // Small delay to allow moving to dropdown
  };

  const handleDropdownClick = (label) => {
    setOpenMenu((m) => (m === label ? null : label));
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenMenu(null);
    setMobileOpen(null);
  }, [location.pathname]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <nav className="bg-black border-b border-gray-800 sticky top-0 z-50 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <span className="text-xl md:text-2xl font-bold text-white tracking-[0.18em]">BWGELA</span>
          </Link>
          
          <div className="hidden md:flex flex-1 items-center ml-6">
            {/* Nav Links: spread evenly across the centre */}
            <div className="flex flex-1 items-center justify-evenly px-4 lg:px-8" data-testid="navbar-links">
            {navLinks.map((link, index) => (
              <React.Fragment key={link.label}>
                {link.children ? (
                  <div className="relative" onMouseEnter={() => handleDropdownEnter(link.label)} onMouseLeave={handleDropdownLeave}>
                    <div className={`flex items-center text-sm rounded-md whitespace-nowrap ${groupActive(link) ? 'text-white font-medium' : 'text-gray-200 hover:text-white'}`}>
                      {link.path && (
                        <Link to={link.path} onClick={() => onNav(link.path, 'navbar')} className="pl-2 py-1.5" data-testid={`navbar-${slug(link.label)}-link`}>{link.label}</Link>
                      )}
                      <button
                        data-testid={`navbar-${slug(link.label)}-toggle`}
                        onClick={() => handleDropdownClick(link.label)}
                        aria-expanded={openMenu === link.label}
                        aria-label={`${link.label} menu`}
                        className={`py-1.5 pr-2 flex items-center gap-0.5 ${link.path ? 'pl-0.5' : 'pl-2'}`}
                      >
                        {!link.path && link.label}
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openMenu === link.label ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    {openMenu === link.label && (
                      <div data-testid={`navbar-${slug(link.label)}-menu`} className="absolute left-0 top-full pt-2 z-50">
                        <div className="bg-black border border-gray-800 rounded-md shadow-lg py-1 min-w-[160px]">
                          {link.children.map((c) => (
                            <Link key={c.path} to={c.path} onClick={() => { onNav(c.path, 'navbar'); setOpenMenu(null); }}
                              data-testid={`navbar-sub-${slug(c.label)}`}
                              className={`block px-4 py-2 text-sm whitespace-nowrap ${location.pathname.startsWith(c.path) ? 'text-white font-medium' : 'text-gray-200 hover:text-white hover:bg-[#1a1a1a]'}`}>
                              {c.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link to={link.path} onClick={() => onNav(link.path, 'navbar')} data-testid={link.icon ? 'navbar-blogcast-link' : undefined}>
                    <button
                      className={`px-2 py-1.5 text-sm rounded-md transition-all whitespace-nowrap flex items-center gap-1 ${
                        location.pathname === link.path ? 'text-white font-medium' : 'text-gray-200 hover:text-white'
                      }`}
                    >
                      {link.icon && <link.icon className="h-3.5 w-3.5" />}
                      {link.label}
                    </button>
                  </Link>
                )}
                {index < navLinks.length - 1 && (
                  <span aria-hidden="true" className="h-4 w-px bg-[#F5F5DC]/75 self-center" />
                )}
              </React.Fragment>
            ))}
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center space-x-0.5">
            {isAuthenticated ? (
              <>
                {execMe?.is_contact && (
                  <Link to="/company-dashboard" onClick={scrollToTop}>
                    <button data-testid="navbar-company-dashboard-link" className="px-3 py-1.5 text-sm whitespace-nowrap rounded-full border border-[#F5F5DC] text-[#F5F5DC] hover:bg-[#F5F5DC] hover:text-[#0a0a0a] transition-colors">
                      Company Dashboard
                    </button>
                  </Link>
                )}
                {execMe?.enrolled && (
                  <Link to="/executive/masterclasses" onClick={scrollToTop}>
                    <button data-testid="navbar-my-masterclasses-link" className="px-3 py-1.5 text-sm whitespace-nowrap rounded-full bg-[#F5F5DC] text-[#0a0a0a] hover:bg-white transition-colors">
                      My Masterclasses
                    </button>
                  </Link>
                )}
                {corporate && (
                  <Link to="/my-aon" onClick={scrollToTop}>
                    <button data-testid="navbar-my-aon-link" className="px-3 py-1.5 text-sm whitespace-nowrap rounded-full bg-teal-300 text-[#0a0a0a] hover:bg-teal-200 transition-colors">
                      My AON Course
                    </button>
                  </Link>
                )}
                {user?.is_admin && (
                  <Link to="/admin" onClick={scrollToTop}>
                    <button
                      data-testid="navbar-admin-link"
                      className="px-3 py-1.5 text-sm text-[#F5F5DC] hover:text-white rounded-md transition-all"
                    >
                      Admin
                    </button>
                  </Link>
                )}
                <Link to="/settings" onClick={scrollToTop}>
                  <button
                    data-testid="navbar-settings-link"
                    className="px-3 py-1.5 text-sm text-gray-200 hover:text-white rounded-md transition-all"
                  >
                    Settings
                  </button>
                </Link>
                <button
                  data-testid="navbar-logout-btn"
                  onClick={logout}
                  className="px-3 py-1.5 text-sm text-gray-400 hover:text-white rounded-md transition-all flex items-center"
                >
                  <LogOut className="h-4 w-4 mr-1" />
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={openLoginModal}
                  data-testid="navbar-login-btn"
                  className="ml-1 px-2.5 py-1.5 text-sm text-gray-200 hover:text-white border border-gray-600 hover:border-gray-400 rounded-md transition-all flex items-center space-x-1 whitespace-nowrap"
                >
                  <LogIn className="h-4 w-4" />
                  <span>Login</span>
                </button>
                <button
                  onClick={openRegisterModal}
                  data-testid="navbar-signup-btn"
                  className="px-2.5 py-1.5 text-sm bg-white text-black hover:bg-gray-200 rounded-md transition-all flex items-center space-x-1 whitespace-nowrap"
                >
                  <UserPlus className="h-4 w-4" />
                  <span>Sign Up</span>
                </button>
              </>
            )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              data-testid="navbar-mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-400 hover:text-white p-2"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-black border-b border-gray-800 shadow-lg z-50">
          <div className="px-4 py-4 space-y-2">
            {/* All Nav Links */}
            {navLinks.map((link) => link.children ? (
              <div key={link.label}>
                <div className={`flex items-center justify-between w-full rounded-md ${groupActive(link) ? 'text-white font-medium' : 'text-gray-200'}`}>
                  {link.path ? (
                    <Link to={link.path} onClick={() => { onNav(link.path, 'navbar_mobile'); setMobileMenuOpen(false); }} className="flex-1 px-4 py-2 hover:text-white" data-testid={`navbar-${slug(link.label)}-link-mobile`}>{link.label}</Link>
                  ) : (
                    <button onClick={() => setMobileOpen((m) => (m === link.label ? null : link.label))} className="flex-1 text-left px-4 py-2 hover:text-white">{link.label}</button>
                  )}
                  <button
                    data-testid={`navbar-${slug(link.label)}-toggle-mobile`}
                    aria-label={`${link.label} menu`}
                    onClick={() => setMobileOpen((m) => (m === link.label ? null : link.label))}
                    className="px-4 py-2 hover:text-white"
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileOpen === link.label ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {mobileOpen === link.label && link.children.map((c) => (
                  <Link key={c.path} to={c.path}
                    onClick={() => { onNav(c.path, 'navbar_mobile'); setMobileMenuOpen(false); }}
                    data-testid={`navbar-sub-${slug(c.label)}-mobile`}
                    className={`block pl-8 pr-4 py-2 rounded-md ${location.pathname.startsWith(c.path) ? 'text-white font-medium' : 'text-gray-300 hover:text-white'}`}>
                    {c.label}
                  </Link>
                ))}
              </div>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => { onNav(link.path, 'navbar_mobile'); setMobileMenuOpen(false); }}
                data-testid={link.icon ? 'navbar-blogcast-link-mobile' : undefined}
                className={`flex items-center gap-2 px-4 py-2 rounded-md ${
                  location.pathname === link.path ? 'text-white font-medium' : 'text-gray-200 hover:text-white'
                }`}
              >
                {link.icon && <link.icon className="h-4 w-4" />}
                {link.label}
              </Link>
            ))}

            <div className="border-t border-gray-800 pt-4 mt-4">
              {isAuthenticated ? (
                <>
                  {execMe?.is_contact && (
                    <Link to="/company-dashboard" onClick={() => setMobileMenuOpen(false)} data-testid="navbar-company-dashboard-link-mobile"
                      className="flex items-center w-full px-4 py-2 text-[#F5F5DC] hover:text-white rounded-md">
                      Company Dashboard
                    </Link>
                  )}
                  {execMe?.enrolled && (
                    <Link to="/executive/masterclasses" onClick={() => setMobileMenuOpen(false)} data-testid="navbar-my-masterclasses-link-mobile"
                      className="flex items-center w-full px-4 py-2 text-[#F5F5DC] hover:text-white rounded-md">
                      My Masterclasses
                    </Link>
                  )}
                  {corporate && (
                    <Link to="/my-aon" onClick={() => setMobileMenuOpen(false)} data-testid="navbar-my-aon-link-mobile"
                      className="flex items-center w-full px-4 py-2 text-teal-300 hover:text-teal-200 rounded-md">
                      My AON Course
                    </Link>
                  )}
                  {user?.is_admin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center w-full px-4 py-2 text-[#F5F5DC] hover:text-white rounded-md"
                    >
                      Admin
                    </Link>
                  )}
                  <Link
                    to="/settings"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center w-full px-4 py-2 text-gray-200 hover:text-white rounded-md"
                  >
                    Settings
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-gray-200 hover:text-white rounded-md"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      openLoginModal();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-gray-200 hover:text-white rounded-md"
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    <span>Login</span>
                  </button>
                  <button
                    onClick={() => {
                      openRegisterModal();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-gray-200 hover:text-white rounded-md"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    <span>Sign Up</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
        initialMode={authMode}
      />
      <AonBackBar isAuthenticated={isAuthenticated} corporate={corporate} />
    </nav>
  );
};

export default Navbar;
